import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  lerNumero, metro, type FuroSpt, type Vocabulario,
} from '../../api/spt';
import { hojeIso } from '../../formato';

/**
 * O rodapé do furo — onda 356 (RN-276, S06, S07, D11, D13). Avanço (TC, TH,
 * CA, REV), leituras do nível d'água, critério de paralisação (lista
 * fechada), fenômenos (lista fechada), recomendações, a observação montada
 * e as pendências que travam o envio à verificação.
 */

const METODOS = [
  ['TC', 'Trado cavadeira (TC)'], ['TH', 'Trado helicoidal (TH)'],
  ['CA', 'Circulação de água (CA)'], ['REV', 'Revestimento (REV)'],
] as const;

type Gravar = {
  furo: (c: Record<string, unknown>) => Promise<void>;
  avanco: (a: { metodo: string; de: number; ate: number }[]) => Promise<void>;
  na: (n: { data: string; hora: string | null; seco: boolean; profundidade: number | null; noBoletim: boolean; abertoAte?: number | null }[]) => Promise<void>;
  fenomenos: (f: { tipo: string; de: number; ate: number | null; dado: number | null }[]) => Promise<void>;
  recomendacoes: (r: string[]) => Promise<void>;
};

const txt = (n: number | null | undefined) => (n == null ? '' : metro(n));

export function RodapeDoFuro({ furo, vocab, gravar }: { furo: FuroSpt; vocab: Vocabulario; gravar: Gravar }) {
  const ed = furo.editavel;
  const [av, setAv] = useState<Record<string, { de: string; ate: string }>>({});
  const [na, setNa] = useState(furo.na);
  const [fen, setFen] = useState(furo.fenomenos.map((f) => ({ tipo: f.tipo, de: txt(f.de), ate: txt(f.ate), dado: f.dado == null ? '' : String(f.dado) })));
  useEffect(() => {
    const m: Record<string, { de: string; ate: string }> = {};
    for (const [k] of METODOS) {
      const a = furo.avanco.find((x) => x.metodo === k);
      m[k] = { de: txt(a?.de), ate: txt(a?.ate) };
    }
    setAv(m);
    setNa(furo.na);
    setFen(furo.fenomenos.map((f) => ({ tipo: f.tipo, de: txt(f.de), ate: txt(f.ate), dado: f.dado == null ? '' : String(f.dado) })));
  }, [furo]);

  /* 361: o «de» digitado sumia — gravar com a linha pela metade mandava a lista sem ela, e o furo que voltava
     apagava o campo. Só grava quando toda linha está completa (de e até) ou vazia. */
  const gravarAvanco = () => {
    const linhas = METODOS.map(([k]) => ({ metodo: k, de: lerNumero(av[k]?.de ?? ''), ate: lerNumero(av[k]?.ate ?? '') }));
    const ok = (n: number | null) => n != null && !Number.isNaN(n);
    if (linhas.some((a) => ok(a.de) !== ok(a.ate))) return;
    void gravar.avanco(linhas.filter((a) => ok(a.de)) as { metodo: string; de: number; ate: number }[]);
  };
  const gravarNa = (lista = na) => void gravar.na(lista.map((l) => ({
    data: l.data, hora: l.hora, seco: l.seco, profundidade: l.seco ? null : l.profundidade, noBoletim: l.no_boletim,
    abertoAte: l.aberto_ate ?? null,
  })));
  const gravarFen = (lista = fen) => {
    /* 361: a linha nova sem «de» não grava (antes saía da lista e sumia da tela ao voltar o furo). */
    if (lista.some((f) => !f.tipo || lerNumero(f.de) == null)) return;
    void gravar.fenomenos(lista.map((f) => ({
      tipo: f.tipo, de: lerNumero(f.de)!, ate: lerNumero(f.ate), dado: lerNumero(f.dado),
    })));
  };
  const tipo = (c: string) => vocab.fenomenos.find((t) => t.codigo === c);
  const criterio = vocab.criterios.find((c) => c.codigo === furo.criterio_paralisacao);
  const observacao = [
    ...furo.fenomenos.map((f) => `${f.texto}.`),
    ...vocab.recomendacoes.filter((r) => furo.recomendacoes.includes(r.codigo)).map((r) => r.texto),
  ].join(' ');
  const hoje = hojeIso();

  return (
    <div className="spt-rodape">
      <section className="cartao">
        <h3>Avanço</h3>
        {METODOS.map(([k, nome]) => (
          <div key={k} className="spt-linha-avanco">
            <span>{nome}</span>
            <input className="campo" aria-label={`${nome} — de (m)`} placeholder="de" disabled={!ed}
              value={av[k]?.de ?? ''} onChange={(e) => setAv({ ...av, [k]: { ...av[k], de: e.target.value } })} onBlur={gravarAvanco} />
            <input className="campo" aria-label={`${nome} — até (m)`} placeholder="até" disabled={!ed}
              value={av[k]?.ate ?? ''} onChange={(e) => setAv({ ...av, [k]: { ...av[k], ate: e.target.value } })} onBlur={gravarAvanco} />
          </div>
        ))}
      </section>

      <section className="cartao">
        <h3>Nível d'água</h3>
        {na.map((l, i) => (
          <div key={i} className="spt-linha-form spt-linha-na">
            <input className="campo" type="date" aria-label={`Data da leitura ${i + 1}`} disabled={!ed} value={l.data}
              onChange={(e) => setNa(na.map((x, k) => (k === i ? { ...x, data: e.target.value } : x)))} onBlur={() => gravarNa()} />
            {/* 362 · a hora da leitura (a de 12 h depois do término é conferida por ela, 5.2.5.5). */}
            <input className="campo spt-hora" type="time" aria-label={`Hora da leitura ${i + 1}`} disabled={!ed} value={l.hora ?? ''}
              onChange={(e) => setNa(na.map((x, k) => (k === i ? { ...x, hora: e.target.value || null } : x)))} onBlur={() => gravarNa()} />
            <label className="spt-marca"><input type="checkbox" checked={l.seco} disabled={!ed}
              onChange={(e) => { const n = na.map((x, k) => (k === i ? { ...x, seco: e.target.checked, profundidade: e.target.checked ? null : (x.profundidade ?? 1) } : x)); setNa(n); gravarNa(n); }} />Seco</label>
            {/* 361: o texto fica como se digita (antes virava número a cada tecla e «3,» se apagava); grava ao sair. */}
            <input key={`${i}-${l.seco}-${l.profundidade}`} className="campo" aria-label={`Profundidade do NA na leitura ${i + 1} (m)`}
              disabled={!ed || l.seco} defaultValue={l.seco ? '' : txt(l.profundidade)}
              onBlur={(e) => {
                const v = lerNumero(e.target.value);
                if (v === l.profundidade) return;
                const n = na.map((x, k) => (k === i ? { ...x, profundidade: v } : x)); setNa(n); gravarNa(n);
              }} />
            <label className="spt-marca"><input type="radio" name="spt-na-boletim" checked={l.no_boletim} disabled={!ed}
              onChange={() => { const n = na.map((x, k) => ({ ...x, no_boletim: k === i })); setNa(n); gravarNa(n); }} />No gráfico</label>
            {/* 362 · NBR 6484 5.2.5.5: até onde o furo está aberto (a leitura de 12 h depois do término pede). */}
            <input key={`a${i}-${l.aberto_ate}`} className="campo" aria-label={`Furo aberto até, na leitura ${i + 1} (m)`}
              placeholder="aberto até (m)" disabled={!ed} defaultValue={txt(l.aberto_ate)}
              onBlur={(e) => {
                const v = lerNumero(e.target.value);
                if (v === (l.aberto_ate ?? null)) return;
                const n = na.map((x, k) => (k === i ? { ...x, aberto_ate: v } : x)); setNa(n); gravarNa(n);
              }} />
          </div>
        ))}
        {ed && na.length < 5 && (
          <button type="button" className="botao secundario"
            onClick={() => { const n = [...na, { data: hoje, hora: null, seco: true, profundidade: null, no_boletim: na.length === 0 }]; setNa(n); gravarNa(n); }}>
            + Leitura
          </button>
        )}
      </section>

      <section className="cartao">
        <h3>Critério de paralisação</h3>
        <select className="campo" aria-label="Critério de paralisação" disabled={!ed} value={furo.criterio_paralisacao ?? ''}
          onChange={(e) => void gravar.furo({ criterioParalisacao: e.target.value || null })}>
          <option value="">Escolha…</option>
          {vocab.criterios.map((c) => <option key={c.codigo} value={c.codigo}>{c.nome}</option>)}
        </select>
        {criterio && <p className="spt-previa">{criterio.texto_padrao}{criterio.conferido ? '' : ' (texto a conferir pelo RT)'}</p>}
        {furo.criterio_paralisacao === 'trepano' && (
          <div className="spt-linha-form">
            {/* 362 · NBR 6484 5.2.4.4–5.2.4.5: o avanço do trépano em mm, em cada período de 10 min; impenetrável é < 50 mm em todos. */}
            <span>Ensaio de avanço por circulação de água (mm por 10 min; impenetrável: &lt; 50 em cada)</span>
            {[1, 2, 3].map((k) => (
              <input key={k} className="campo" aria-label={`Avanço no ${k}º período de 10 min (mm)`} placeholder={`${k}º período (mm)`}
                disabled={!ed} defaultValue={txt((furo as unknown as Record<string, number | null>)[`lavagem_${k}`])}
                onBlur={(e) => {
                  const l = [1, 2, 3].map((j) => (j === k ? lerNumero(e.target.value) : (furo as unknown as Record<string, number | null>)[`lavagem_${j}`]));
                  void gravar.furo({ lavagem: l });
                }} />
            ))}
          </div>
        )}
      </section>

      <section className="cartao spt-largo">
        <h3>Fenômenos observados</h3>
        {fen.map((f, i) => {
          const t = tipo(f.tipo);
          return (
            <div key={i} className="spt-linha-form spt-linha-fen">
              <select className="campo" aria-label="Tipo de fenômeno" disabled={!ed} value={f.tipo}
                onChange={(e) => { const n = fen.map((x, k) => (k === i ? { ...x, tipo: e.target.value } : x)); setFen(n); gravarFen(n); }}>
                {vocab.fenomenos.map((o) => <option key={o.codigo} value={o.codigo}>{o.nome}</option>)}
              </select>
              <input className="campo" aria-label="De (m)" placeholder="de (m)" disabled={!ed} value={f.de}
                onChange={(e) => setFen(fen.map((x, k) => (k === i ? { ...x, de: e.target.value } : x)))} onBlur={() => gravarFen()} />
              {t?.faixa
                ? <input className="campo" aria-label="Até (m)" placeholder="até (m)" disabled={!ed} value={f.ate}
                    onChange={(e) => setFen(fen.map((x, k) => (k === i ? { ...x, ate: e.target.value } : x)))} onBlur={() => gravarFen()} />
                : <span className="sub">pontual</span>}
              {t?.dado_proprio
                ? <input className="campo" aria-label={t.dado_proprio} placeholder={t.dado_proprio} disabled={!ed} value={f.dado}
                    onChange={(e) => setFen(fen.map((x, k) => (k === i ? { ...x, dado: e.target.value } : x)))} onBlur={() => gravarFen()} />
                : <span />}
              {ed && <button type="button" className="botao discreto" tabIndex={-1} aria-label={`Excluir o fenômeno ${i + 1}`}
                onClick={() => { const n = fen.filter((_, k) => k !== i); setFen(n); gravarFen(n); }}>Excluir</button>}
            </div>
          );
        })}
        {ed && (
          <button type="button" className="botao secundario"
            onClick={() => setFen([...fen, { tipo: 'perda_total', de: '', ate: '', dado: '' }])}>+ Fenômeno</button>
        )}
        <h4>Recomendações</h4>
        {vocab.recomendacoes.map((r) => {
          const on = furo.recomendacoes.includes(r.codigo);
          return (
            <label key={r.codigo} className="spt-marca">
              <input type="checkbox" checked={on} disabled={!ed}
                onChange={() => void gravar.recomendacoes(on ? furo.recomendacoes.filter((x) => x !== r.codigo) : [...furo.recomendacoes, r.codigo])} />
              {r.texto}
            </label>
          );
        })}
        <h4>Observação que sai no boletim</h4>
        <p className="spt-previa">{observacao || 'Sem observação.'}</p>
      </section>

      <section className="cartao" aria-live="polite">
        <h3>Pendências para enviar · {furo.pendencias.length}</h3>
        {furo.pendencias.length === 0
          ? <p className="spt-sem-pendencia">● Nada pendente neste furo.</p>
          : <ul className="spt-pendencias">{furo.pendencias.map((p, i) => (
            <li key={`${p.codigo}${i}`}>▲ {p.texto}
              {/* 366: a RN se lança na Edição rápida da campanha (com a permissão do SPT). */}
              {p.codigo === 'sem_rn' && <> — <Link className="spt-pendencia-rn" to={`/spt/${furo.campanha_id}/edicao`}>lançar a RN na Edição rápida</Link></>}
            </li>
          ))}</ul>}
      </section>
    </div>
  );
}
